import {
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
} from "./skillFichaHelpers";

const SIN_TEMPORADA = "Sin temporada";
const SIN_CLIENTE = "Sin cliente";

export function taskClientLabel(row) {
  return (
    row?.CustomerName ||
    (row?.CustomerId ? `Cliente #${row.CustomerId}` : null) ||
    row?.SkillSlug ||
    SIN_CLIENTE
  );
}

export function taskSeasonLabel(row) {
  const t = String(row?.Temporada || "").trim();
  return t || SIN_TEMPORADA;
}

export function searchStatusLabel(status) {
  const map = {
    draft: "Borrador",
    running: "Buscando…",
    done: "Listo",
    error: "Error",
    cancelled: "Cancelado",
  };
  return map[status] || status || "—";
}

export function moodboardStatusLabel(status) {
  const map = {
    draft: "Borrador",
    ready: "PDF listo",
  };
  return map[status] || status || "—";
}

/**
 * groupBy: "season" → Temporada → Cliente → DISEÑOS + SEARCHS + MOODBOARDS
 * groupBy: "client" → Cliente → Temporada → DISEÑOS + SEARCHS + MOODBOARDS
 *
 * @param {object[]} rows tasks (encargos / diseños)
 * @param {"season"|"client"} groupBy
 * @param {object[]} searches library searches
 * @param {object[]} moodboards library moodboards
 */
export function buildAgentTasksTree(
  rows = [],
  groupBy = "season",
  searches = [],
  moodboards = [],
) {
  const filtered = Array.isArray(rows) ? rows : [];
  const searchRows = Array.isArray(searches) ? searches : [];
  const moodboardRows = Array.isArray(moodboards) ? moodboards : [];
  const rootMap = new Map();

  function ensureL2(L1, L2, kinds) {
    if (!rootMap.has(L1)) {
      rootMap.set(L1, {
        id: `l1:${L1}`,
        label: L1,
        kind: kinds.l1,
        children: new Map(),
        tasks: [],
      });
    }
    const n1 = rootMap.get(L1);
    if (!n1.children.has(L2)) {
      n1.children.set(L2, {
        id: `l2:${L1}::${L2}`,
        label: L2,
        kind: kinds.l2,
        tasks: [],
        searches: [],
        moodboards: [],
      });
    }
    return n1.children.get(L2);
  }

  const kinds =
    groupBy === "client"
      ? { l1: "client", l2: "season" }
      : { l1: "season", l2: "client" };

  for (const row of filtered) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).tasks.push(row);
  }

  for (const row of searchRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).searches.push(row);
  }

  for (const row of moodboardRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).moodboards.push(row);
  }

  const sortLabel = (a, b) =>
    String(a).localeCompare(String(b), "es", {
      numeric: true,
      sensitivity: "base",
    });

  const roots = [...rootMap.values()]
    .map((n1) => {
      const children = [...n1.children.values()]
        .map((n2) => {
          const tasks = [...n2.tasks].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const searchesSorted = [...(n2.searches || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const moodboardsSorted = [...(n2.moodboards || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const ctxCustomerId =
            tasks.find((t) => t.CustomerId)?.CustomerId ||
            searchesSorted.find((s) => s.CustomerId)?.CustomerId ||
            moodboardsSorted.find((m) => m.CustomerId)?.CustomerId ||
            null;
          const Temporada = groupBy === "client" ? n2.label : n1.label;
          const CustomerName = groupBy === "client" ? n1.label : n2.label;
          const branchCtx = {
            seasonLabel: Temporada,
            clientLabel: CustomerName,
            Temporada,
            CustomerName,
            CustomerId: ctxCustomerId,
          };
          const disenosNode = {
            id: `${n2.id}::disenos`,
            label: "DISEÑOS",
            kind: "disenos",
            tasks,
            searches: [],
            moodboards: [],
            count: tasks.length,
            ...branchCtx,
          };
          const searchsNode = {
            id: `${n2.id}::searchs`,
            label: "SEARCHS",
            kind: "searchs",
            searches: searchesSorted,
            tasks: [],
            count: searchesSorted.length,
            ...branchCtx,
          };
          const moodboardsNode = {
            id: `${n2.id}::moodboards`,
            label: "MOODBOARDS",
            kind: "moodboards",
            moodboards: moodboardsSorted,
            tasks: [],
            count: moodboardsSorted.length,
            ...branchCtx,
          };
          return {
            ...n2,
            tasks: [],
            searches: searchesSorted,
            moodboards: moodboardsSorted,
            disenosNode,
            searchsNode,
            moodboardsNode,
            count:
              tasks.length + searchesSorted.length + moodboardsSorted.length,
          };
        })
        .sort((a, b) => sortLabel(a.label, b.label));
      const count = children.reduce((s, c) => s + c.count, 0);
      return { ...n1, children, count };
    })
    .sort((a, b) => {
      if (a.label === SIN_TEMPORADA || a.label === SIN_CLIENTE) return 1;
      if (b.label === SIN_TEMPORADA || b.label === SIN_CLIENTE) return -1;
      return sortLabel(a.label, b.label);
    });

  return roots;
}

/**
 * IDs de nodos a expandir para revelar un moodboard / search / task en el árbol.
 */
export function treeExpandIdsForSelection(
  tree,
  { moodboardId, searchId, taskId } = {},
) {
  const mb =
    moodboardId != null && String(moodboardId) !== ""
      ? Number(moodboardId)
      : null;
  const se =
    searchId != null && String(searchId) !== "" ? Number(searchId) : null;
  const tk = taskId != null && String(taskId) !== "" ? Number(taskId) : null;
  if (
    (mb == null || !Number.isFinite(mb)) &&
    (se == null || !Number.isFinite(se)) &&
    (tk == null || !Number.isFinite(tk))
  ) {
    return [];
  }
  for (const n1 of tree || []) {
    for (const n2 of n1.children || []) {
      if (
        mb != null &&
        (n2.moodboards || []).some((m) => Number(m.id) === mb)
      ) {
        return [n1.id, n2.id, n2.moodboardsNode?.id].filter(Boolean);
      }
      if (se != null && (n2.searches || []).some((s) => Number(s.id) === se)) {
        return [n1.id, n2.id, n2.searchsNode?.id].filter(Boolean);
      }
      if (
        tk != null &&
        (n2.disenosNode?.tasks || n2.tasks || []).some(
          (t) => Number(t.id) === tk,
        )
      ) {
        return [n1.id, n2.id, n2.disenosNode?.id].filter(Boolean);
      }
    }
  }
  return [];
}

export {
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
  SIN_TEMPORADA,
  SIN_CLIENTE,
};
